<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRaw,
  watch
} from 'vue'

import { randomId } from '@/lib/randomId'

import { useCmkErrorBoundary } from '@/components/CmkErrorBoundary'
import CmkErrorAlert from '@/components/CmkErrorBoundary/CmkErrorAlert.vue'
import CmkIcon from '@/components/CmkIcon'

import DashboardBreadcrumb from '@/dashboard/components/DashboardBreadcrumb/DashboardBreadcrumb.vue'
import DashboardComponent from '@/dashboard/components/DashboardComponent.vue'
import DashboardFilterSettings from '@/dashboard/components/DashboardFilterSettings/DashboardFilterSettings.vue'
import DashboardMenuHeader from '@/dashboard/components/DashboardMenuHeader/DashboardMenuHeader.vue'
import type { SelectedDashboard } from '@/dashboard/components/DashboardMenuHeader/types'
import { offsetInitialPosition } from '@/dashboard/components/RelativeGrid/utils'
import { createWidgetLayout } from '@/dashboard/components/ResponsiveGrid/composables/useResponsiveGridLayout'
import AddWidgetDialog from '@/dashboard/components/WidgetWorkflow/StarterDialog/AddWidgetDialog.vue'
import AddWidgetPage from '@/dashboard/components/WidgetWorkflow/StarterPage/AddWidgetPage.vue'
import { getDashboardWidgetWorkflows } from '@/dashboard/components/WidgetWorkflow/WidgetWorkflowTypes'
import CloneDashboardWizard from '@/dashboard/components/Wizard/CloneDashboardWizard.vue'
import CreateDashboardWizard from '@/dashboard/components/Wizard/CreateDashboardWizard.vue'
import WizardSelector from '@/dashboard/components/WizardSelector/WizardSelector.vue'
import { widgetTypeToSelectorMatcher } from '@/dashboard/components/WizardSelector/utils.ts'
import type {
  ConfiguredFilters,
  FilterDefinition,
  FilterGroups
} from '@/dashboard/components/filter/types.ts'
import { useDashboardFilters } from '@/dashboard/composables/useDashboardFilters.ts'
import { useDashboardVisualTitle } from '@/dashboard/composables/useDashboardVisualTitle.ts'
import { useDashboardWidgets } from '@/dashboard/composables/useDashboardWidgets.ts'
import { useDashboardsManager } from '@/dashboard/composables/useDashboardsManager.ts'
import { useProvideDashboardConstants } from '@/dashboard/composables/useProvideDashboardConstants'
import { useProvideMissingRuntimeFiltersAction } from '@/dashboard/composables/useProvideMissingRuntimeFiltersAction.ts'
import { useProvideVisualInfos } from '@/dashboard/composables/useProvideVisualInfos'
import { useComputeWidgetTitles } from '@/dashboard/composables/useWidgetTitles'
import {
  type ContentResponsiveGrid,
  type DashboardGeneralSettings,
  type DashboardKey,
  DashboardLayout,
  type DashboardMetadata,
  type DashboardModel
} from '@/dashboard/types/dashboard.ts'
import { RuntimeFilterMode } from '@/dashboard/types/filter.ts'
import { urlParamsKey } from '@/dashboard/types/injectionKeys.ts'
import type { BreadcrumbItem } from '@/dashboard/types/page'
import type { DashboardPageProperties } from '@/dashboard/types/page.ts'
import type {
  WidgetContent,
  WidgetFilterContext,
  WidgetGeneralSettings,
  WidgetLayout,
  WidgetSpec
} from '@/dashboard/types/widget'
import { dashboardAPI, urlHandler } from '@/dashboard/utils.ts'

import CloneSuccessAlert from './components/CloneSuccessAlert.vue'
import DashboardSettingsWizard from './components/Wizard/DashboardSettingsWizard.vue'
import DashboardSharingWizard from './components/Wizard/wizards/dashboard-sharing/DashboardSharingWizard.vue'

// eslint-disable-next-line @typescript-eslint/naming-convention
const { CmkErrorBoundary } = useCmkErrorBoundary()

const props = defineProps<DashboardPageProperties>()

const dbAppRef = ref<HTMLElement | null>(null)
const isDashboardLoading = ref(false)
const loadingError = ref<Error | null>(null)
const isDashboardEditingMode = ref(props.mode === 'edit_layout' && !!props.dashboard)
const openDashboardFilterSettings = ref(false)
const openDashboardSettings = ref(props.mode === 'edit_settings')
const openAddWidgetDialog = ref(false)
const openDashboardCreationDialog = ref(props.mode === 'create')
const openDashboardCloneDialog = ref(props.mode === 'clone')
const isCloning = ref(false)
const openDashboardShareDialog = ref(false)
const openWizard = ref(false)
const selectedWizard = ref('')
const widgetToEdit = ref<string | null>(null)
const selectedDashboardBreadcrumb = ref<BreadcrumbItem[] | null>(null)
const showCloneSuccessAlert = ref(false)

watch(showCloneSuccessAlert, async () => {
  await nextTick()
  window.dispatchEvent(new Event('resize'))
})

const dashboardFilterSettingsStartingWindow = ref<'runtime-filters' | 'filter-settings'>(
  'runtime-filters'
)

const filterCollection = ref<Record<string, FilterDefinition> | null>(null)
provide('filterCollection', filterCollection)

const filterGroups = ref<FilterGroups | null>(null)
provide('filterGroups', filterGroups)

useProvideVisualInfos()

// So far, this is only needed and used by the DashboardContentNtop component
provide(urlParamsKey, props.url_params)

const dashboardsManager = useDashboardsManager()
useProvideDashboardConstants(dashboardsManager.constants)

onBeforeMount(async () => {
  const [filterResp, filterGroupsResp] = await Promise.all([
    dashboardAPI.listFilterCollection(),
    dashboardAPI.listFilterGroups()
  ])

  const filterDefsRecord: Record<string, FilterDefinition> = {}
  filterResp.value.forEach((filter) => {
    filterDefsRecord[filter.id!] = filter
  })
  filterCollection.value = filterDefsRecord

  const groups: FilterGroups = {}
  filterGroupsResp.value.forEach((group) => {
    groups[group.id!] = group
  })
  filterGroups.value = groups

  if (props.dashboard) {
    const dashboard = props.dashboard
    const key: DashboardKey = {
      name: dashboard.metadata.name,
      owner: dashboard.metadata.owner || '' // built-in conversion: null -> ""
    }
    isDashboardLoading.value = true
    try {
      await dashboardsManager.loadDashboard(key, dashboard.metadata.layout_type as DashboardLayout)
    } catch (e) {
      loadingError.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      isDashboardLoading.value = false
    }

    if (dashboardsManager.activeDashboard.value?.metadata?.is_built_in) {
      isDashboardEditingMode.value = false
    }

    dashboardFilters.handleApplyRuntimeFilters(dashboard.filter_context.context)
    if (!dashboardFilters.areAllMandatoryFiltersApplied.value) {
      openDashboardFilterSettings.value = true
    }

    const cloneSuccess = new URLSearchParams(window.location.search).get('clone_success')
    if (cloneSuccess === '1') {
      showCloneSuccessAlert.value = true
      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete('clone_success')
      urlHandler.updateCurrentUrl(cleanUrl)
    }
  }
})

const dashboardFilters = useDashboardFilters(
  computed(() => dashboardsManager.activeDashboard.value?.model.filter_context)
)
const dashboardWidgets = useDashboardWidgets(
  computed(() => dashboardsManager.activeDashboard.value?.model.content.widgets)
)
const widgetTitles = useComputeWidgetTitles(
  dashboardFilters.baseFilters,
  dashboardWidgets.widgetCores
)
const dashboardWidgetWorkflows = getDashboardWidgetWorkflows(props.available_features.ntop_active)

useProvideMissingRuntimeFiltersAction(dashboardFilters.areAllMandatoryFiltersApplied, () => {
  openDashboardFilterSettings.value = true
})

watch(dashboardsManager.activeDashboard, (value) => {
  if (!value) {
    isDashboardEditingMode.value = false
  }
})

const handlePopState = () => window.location.reload()

onMounted(() => {
  window.addEventListener('popstate', handlePopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState)
})

// This is a workaround to hide the scrollbar on ancestor elements outside
// of this component's control (obviously not ideal). Ideally the page-level scroll behavior would be
// managed by the parent layout (but this is on the python page level so we don't do this)
function setAncestorsEmptyClass(add: boolean) {
  let el = dbAppRef.value?.parentElement
  while (el) {
    el.classList.toggle('db-app--empty-dashboard', add)
    el = el.parentElement
  }
}

/* Set/Remove the page's background gradient when there are no/any widgets */
watch(
  dashboardWidgets.widgetCores,
  (newWidgetCores, oldWidgetCores) => {
    if (Object.entries(newWidgetCores).length === 0) {
      document.body.style.backgroundImage =
        'linear-gradient(to bottom, var(--ux-theme-1), var(--ux-theme-8))'
      setAncestorsEmptyClass(true)
    } else if (
      (!oldWidgetCores || Object.entries(oldWidgetCores).length === 0) &&
      Object.entries(newWidgetCores).length > 0
    ) {
      document.body.style.backgroundImage = 'none'
      setAncestorsEmptyClass(false)
    }
  },
  { immediate: true }
)

const setAsActiveDashboard = async (dashboardKey: DashboardKey, layout: DashboardLayout) => {
  isDashboardLoading.value = true
  loadingError.value = null
  try {
    await dashboardsManager.loadDashboard(dashboardKey, layout)
  } catch (e) {
    loadingError.value = e instanceof Error ? e : new Error(String(e))
  } finally {
    isDashboardLoading.value = false
  }
}

const handleSelectDashboard = async (dashboard: DashboardMetadata) => {
  isDashboardEditingMode.value = false
  showCloneSuccessAlert.value = false

  // Reset runtime filters when switching dashboards via dropdown
  dashboardFilters.handleResetRuntimeFilters()

  const key: DashboardKey = {
    name: dashboard.name,
    owner: dashboard.owner || '' // built-in conversion: null -> ""
  }
  await setAsActiveDashboard(key, dashboard.layout_type as DashboardLayout)
  selectedDashboardBreadcrumb.value =
    dashboardsManager.activeDashboard.value?.metadata?.display?.topic?.breadcrumb ?? null
  urlHandler.pushCurrentUrl(urlHandler.getDashboardUrl(key, {}))

  if (!dashboardFilters.areAllMandatoryFiltersApplied.value) {
    openDashboardFilterSettings.value = true
  }
}

const selectedDashboard = computed(() => {
  if (!dashboardsManager.activeDashboard.value) {
    return null
  }

  return {
    name: dashboardsManager.activeDashboardKey.value!.name,
    owner: dashboardsManager.activeDashboardKey.value!.owner,
    title: dashboardVisualTitle.value,
    type: dashboardsManager.activeDashboard.value.model.type
  } as SelectedDashboard
})

const dashboardVisualTitle = useDashboardVisualTitle(
  computed(() => dashboardsManager.activeDashboard.value?.model ?? null),
  filterCollection,
  dashboardFilters.baseFilters
)

watch(dashboardVisualTitle, (newTitle) => {
  // We only set the title for the current document. If we're inside the index page,
  // the tabs title will be correctly updated via initialize_sidebar() periodically.
  document.title = newTitle
})

const handleWizardSelectorGoBack = () => {
  // when editing, do not go back to widget type selection (and clear the edit state)
  if (widgetToEdit.value) {
    widgetToEdit.value = null
  } else {
    openAddWidgetDialog.value = true
  }
  openWizard.value = false
}

const handleWizardClose = () => {
  widgetToEdit.value = null
  openWizard.value = false
  openAddWidgetDialog.value = false
}

const handleAddWidget = (widgetIdent: string) => {
  if (!dashboardsManager.isInitialized.value) {
    throw new Error('Dashboard not yet initialized.')
  }
  openAddWidgetDialog.value = false
  selectedWizard.value = widgetIdent
  openWizard.value = true
}

function generateWidgetId(widgetContentType: string): string {
  const key = dashboardsManager.activeDashboardKey.value
  if (!key) {
    throw new Error('No active dashboard')
  }
  return randomId(16, `${key.name}-${widgetContentType}`)
}

async function saveDashboard() {
  await dashboardsManager.persistDashboard()
  isDashboardEditingMode.value = false
}

async function cancelEdit() {
  // we overwrite all changes by reloading the dashboard
  isDashboardLoading.value = true
  loadingError.value = null
  try {
    await dashboardsManager.loadDashboard(
      dashboardsManager.activeDashboardKey.value!,
      dashboardsManager.activeDashboard.value!.model.content.layout.type as DashboardLayout
    )
  } catch (e) {
    loadingError.value = e instanceof Error ? e : new Error(String(e))
  } finally {
    isDashboardLoading.value = false
  }
  isDashboardEditingMode.value = false
}

function addWidget(
  content: WidgetContent,
  generalSettings: WidgetGeneralSettings,
  filterContext: WidgetFilterContext
) {
  if (!dashboardsManager.isInitialized.value) {
    throw new Error('Dashboard not yet initialized.')
  }
  const activeDashboard = dashboardsManager.activeDashboard.value!.model as DashboardModel
  const widgetId = generateWidgetId(content.type)
  let layout: WidgetLayout
  if (activeDashboard.content.layout.type === 'responsive_grid') {
    layout = createWidgetLayout(
      activeDashboard.content as ContentResponsiveGrid,
      content.type,
      dashboardsManager.constants.value!
    )
  } else if (activeDashboard.content.layout.type === 'relative_grid') {
    const widgetConstants = dashboardsManager.constants.value!.widgets[content.type]!
    layout = {
      type: 'relative_grid',
      // Add a static vertical offset to reduce the chance of placing
      // the new widget in a way where it covers existing widgets
      position: offsetInitialPosition(widgetConstants.layout.relative.initial_position, {
        horizontal: false,
        vertical: Object.keys(dashboardWidgets.widgetCores.value).length > 0
      }),
      size: widgetConstants.layout.relative.initial_size
    }
  } else {
    throw new Error(`Unknown dashboard layout type: ${activeDashboard.content.layout}`)
  }
  dashboardWidgets.addWidget(widgetId, content, generalSettings, filterContext, layout)
  openWizard.value = false
  isDashboardEditingMode.value = true
}

function editWidget(widgetId: string) {
  if (!dashboardsManager.isInitialized.value) {
    throw new Error('Dashboard not yet initialized.')
  }

  widgetToEdit.value = widgetId
  const widgetSpec = dashboardsManager.activeDashboard.value!.model.content.widgets[widgetId]
  if (!widgetSpec) {
    throw new Error(`Widget with id ${widgetId} not found`)
  }
  selectedWizard.value = widgetTypeToSelectorMatcher(widgetSpec.content.type)
  openWizard.value = true
}

function executeEditWidget(
  widgetId: string,
  content: WidgetContent,
  generalSettings: WidgetGeneralSettings,
  filterContext: WidgetFilterContext
) {
  dashboardWidgets.updateWidget(widgetId, content, generalSettings, filterContext)

  widgetToEdit.value = null
  openWizard.value = false
}

function cloneWidget(oldWidgetId: string, newLayout: WidgetLayout) {
  const oldWidget = dashboardWidgets.getWidget(oldWidgetId)
  if (!oldWidget) {
    throw new Error(`Widget with id ${oldWidgetId} not found`)
  }
  const newWidgetId = generateWidgetId(oldWidget.content.type)
  dashboardWidgets.cloneWidget(oldWidgetId, newWidgetId, newLayout)
}

function getWidgetSpecToEdit(widgetId: string | null): WidgetSpec | null {
  if (!widgetId) {
    return null
  }
  const widget = dashboardsManager.activeDashboard.value!.model.content.widgets[widgetId]
  if (!widget) {
    throw new Error(`Widget with id ${widgetId} not found`)
  }
  return {
    content: widget.content,
    general_settings: widget.general_settings,
    filter_context: widget.filter_context
  }
}

const createDashboard = async (
  dashboardId: string,
  settings: DashboardGeneralSettings,
  layout: DashboardLayout,
  scopeIds: string[]
) => {
  openDashboardCreationDialog.value = false
  isDashboardLoading.value = true
  loadingError.value = null

  let key: DashboardKey
  try {
    // avoid showing the new dashboard, while the dashboard list is loading
    key = await dashboardsManager.createDashboard(
      dashboardId,
      settings,
      layout,
      scopeIds,
      'setDashboardAsActive'
    )
  } catch (e) {
    loadingError.value = e instanceof Error ? e : new Error(String(e))
    isDashboardLoading.value = false
    return
  }

  const updatedDashboardUrl = urlHandler.getDashboardUrl(key, {})
  urlHandler.navigateTo(updatedDashboardUrl)
}

const cloneDashboard = async (
  dashboardId: string,
  generalSettings: DashboardGeneralSettings,
  layout: DashboardLayout
) => {
  const key = dashboardsManager.activeDashboardKey.value
  if (!key) {
    throw new Error('No active dashboard to clone from')
  }
  openDashboardCloneDialog.value = false
  isCloning.value = true
  loadingError.value = null

  let newKey: DashboardKey
  try {
    let newOwner
    if (layout === DashboardLayout.RELATIVE_GRID) {
      const response = await dashboardAPI.cloneAsRelativeGridDashboard(
        key.name,
        key.owner,
        dashboardId,
        generalSettings
      )
      newOwner = response.extensions.owner
    } else {
      const response = await dashboardAPI.cloneAsResponsiveGridDashboard(
        key.name,
        key.owner,
        dashboardId,
        generalSettings
      )
      newOwner = response.extensions.owner
    }

    newKey = {
      name: dashboardId,
      owner: newOwner
    }
  } catch (e) {
    loadingError.value = e instanceof Error ? e : new Error(String(e))
    isCloning.value = false
    return
  }

  const cloneUrl = urlHandler.getDashboardUrl(newKey, {})
  cloneUrl.searchParams.set('clone_success', '1')
  urlHandler.navigateTo(cloneUrl)
}

const dashboardHasFilters = computed(
  () =>
    Object.keys(dashboardsManager.activeDashboard.value?.model?.filter_context?.filters || {})
      .length > 0
)

const dashboardHasRuntimeFilters = computed(() => {
  return (toRaw(dashboardFilters.configuredMandatoryRuntimeFilters.value)?.length ?? 0) > 0
})

const isDashboardEmpty = computed(
  () => Object.entries(dashboardWidgets.widgetCores.value).length === 0
)

const redirectToListDashboardsPage = () => {
  window.location.href = props.links.list_dashboards
}

const handleApplyRuntimeFilters = (filters: ConfiguredFilters, mode: RuntimeFilterMode) => {
  dashboardFilters.handleApplyRuntimeFilters(filters)
  dashboardFilters.setRuntimeFiltersMode(mode)

  const updatedDashboardUrl = urlHandler.updateWithPreserve(
    window.location.href,
    ['name', 'owner'],
    dashboardFilters.runtimeFiltersSearchParams.value
  )
  urlHandler.updateCurrentUrl(updatedDashboardUrl)
}

const handleSaveFilterSettings = async (payload: {
  dashboardFilters: ConfiguredFilters
  mandatoryRuntimeFilters: string[]
}) => {
  dashboardFilters.handleSaveDashboardFilters(payload.dashboardFilters)
  dashboardFilters.handleSaveMandatoryRuntimeFilters(payload.mandatoryRuntimeFilters)
  await dashboardsManager.persistDashboard()
}

function closeFilterSettings() {
  openDashboardFilterSettings.value = false
  dashboardFilterSettingsStartingWindow.value = 'runtime-filters'
}

function openFilterSettings() {
  dashboardFilterSettingsStartingWindow.value = 'filter-settings'
  openDashboardFilterSettings.value = true
}

function openRuntimeFilters() {
  dashboardFilterSettingsStartingWindow.value = 'runtime-filters'
  openDashboardFilterSettings.value = true
}

const updateDashboardSettings = async (
  dashboardName: string,
  generalSettings: DashboardGeneralSettings
) => {
  const key = dashboardsManager.activeDashboardKey.value!
  const newKey: DashboardKey = {
    name: dashboardName,
    owner: key.owner
  }
  dashboardsManager.activeDashboard.value!.model.general_settings = generalSettings
  await dashboardsManager.persistDashboard(dashboardName)
  openDashboardSettings.value = false
  showCloneSuccessAlert.value = false

  const updatedDashboardUrl = urlHandler.getDashboardUrl(
    newKey,
    dashboardFilters.runtimeFiltersSearchParams.value
  )
  urlHandler.updateCurrentUrl(updatedDashboardUrl)
  if (urlHandler.isOnIndexPage()) {
    urlHandler.reloadPage()
  }
}

function deepClone<T>(obj: T): T {
  return structuredClone(obj)
}

const reviewFilters = () => {
  openDashboardShareDialog.value = false
  openDashboardFilterSettings.value = true
}
</script>

<template>
  <CmkErrorBoundary>
    <div ref="dbAppRef" :class="['db-app', { 'db-app--empty': isDashboardEmpty }]">
      <div class="db-app__header">
        <DashboardBreadcrumb
          :selected-dashboard="selectedDashboard ?? null"
          :selected-dashboard-breadcrumb="selectedDashboardBreadcrumb"
          :initial-breadcrumb="initial_breadcrumb"
          :runtime-filters="dashboardFilters.runtimeFiltersSearchParams.value"
        />
        <DashboardMenuHeader
          v-model:is-edit-mode="isDashboardEditingMode"
          :selected-dashboard="selectedDashboard"
          :is-dashboard-loading="
            isDashboardLoading ||
            isCloning ||
            openDashboardCreationDialog ||
            openDashboardCloneDialog
          "
          :can-edit-dashboard="
            dashboardsManager.activeDashboard.value?.metadata?.is_editable ?? false
          "
          :link-user-guide="props.links.user_guide"
          :public-token="dashboardsManager.activeDashboard.value?.model.public_token ?? null"
          :is-empty-dashboard="Object.entries(dashboardWidgets.widgetCores.value).length === 0"
          :runtime-filters="dashboardFilters.runtimeFiltersSearchParams.value"
          @open-runtime-filter="openRuntimeFilters"
          @open-filter-settings="openFilterSettings"
          @open-settings="openDashboardSettings = true"
          @open-clone-workflow="openDashboardCloneDialog = true"
          @open-widget-workflow="openAddWidgetDialog = true"
          @open-share-workflow="openDashboardShareDialog = true"
          @save="saveDashboard"
          @enter-edit="dashboardsManager.activeDashboard.value && (isDashboardEditingMode = true)"
          @cancel-edit="cancelEdit"
          @set-dashboard="handleSelectDashboard"
        />
      </div>
      <div>
        <CreateDashboardWizard
          v-model:open="openDashboardCreationDialog"
          :available-layouts="available_layouts"
          :logged-in-user="logged_in_user"
          @create-dashboard="(...args) => createDashboard(...args)"
          @cancel-creation="redirectToListDashboardsPage"
        />
        <CloneDashboardWizard
          v-if="dashboardsManager.isInitialized.value"
          v-model:open="openDashboardCloneDialog"
          :active-dashboard-id="dashboardsManager.activeDashboardKey.value?.name ?? ''"
          :available-layouts="available_layouts"
          :reference-dashboard-general-settings="
            deepClone(dashboardsManager.activeDashboard.value!.model.general_settings)
          "
          :reference-dashboard-restricted-to-single="
            deepClone(
              dashboardsManager.activeDashboard.value!.model.filter_context.restricted_to_single ||
                []
            )
          "
          :reference-dashboard-layout-type="
            dashboardsManager.activeDashboard.value!.model.content.layout.type as DashboardLayout
          "
          :reference-dashboard-type="dashboardsManager.activeDashboard.value!.model.type"
          :logged-in-user="logged_in_user"
          @clone-dashboard="(...args) => cloneDashboard(...args)"
          @cancel-clone="openDashboardCloneDialog = false"
        />
        <DashboardSharingWizard
          v-if="openDashboardShareDialog && dashboardsManager.activeDashboardKey.value"
          :dashboard-key="dashboardsManager.activeDashboardKey.value!"
          :public-token="dashboardsManager.activeDashboard?.value?.model.public_token || null"
          :dashboard-features="available_features.dashboard_features"
          :has-runtime-filters="dashboardHasRuntimeFilters"
          :may-link-share="permissions.publish_to_all"
          @review-filters="reviewFilters"
          @refresh-dashboard-settings="dashboardsManager.refreshActiveDashboard"
          @close="openDashboardShareDialog = false"
        />
        <AddWidgetDialog
          v-model:open="openAddWidgetDialog"
          :workflow-items="dashboardWidgetWorkflows"
          :dashboard-features="available_features.dashboard_features"
          @select="handleAddWidget"
          @close="openAddWidgetDialog = false"
        />
        <WizardSelector
          v-if="dashboardsManager.constants.value"
          :is-open="openWizard"
          :selected-wizard="selectedWizard"
          :dashboard-key="dashboardsManager.activeDashboardKey.value!"
          :context-filters="dashboardFilters.contextFilters.value || {}"
          :edit-widget-spec="getWidgetSpecToEdit(widgetToEdit ?? null)"
          :edit-widget-id="widgetToEdit"
          :dashboard-features="available_features.dashboard_features"
          @back-button="handleWizardSelectorGoBack"
          @close-wizard="handleWizardClose"
          @add-widget="addWidget"
          @edit-widget="executeEditWidget"
        />
        <DashboardFilterSettings
          v-model:open="openDashboardFilterSettings"
          :configured-dashboard-filters="
            deepClone(dashboardFilters.configuredDashboardFilters.value || {})
          "
          :applied-runtime-filters="deepClone(dashboardFilters.appliedRuntimeFilters.value || {})"
          :configured-mandatory-runtime-filters="[
            ...(dashboardFilters.configuredMandatoryRuntimeFilters.value || [])
          ]"
          :configured-runtime-filters-mode="
            dashboardFilters.runtimeFiltersMode.value || RuntimeFilterMode.OVERRIDE
          "
          :can-edit="dashboardsManager.activeDashboard.value?.metadata?.is_editable ?? false"
          :is-built-in="dashboardsManager.activeDashboard.value?.metadata?.is_built_in ?? false"
          :starting-window="dashboardFilterSettingsStartingWindow"
          @apply-runtime-filters="handleApplyRuntimeFilters"
          @save-filter-settings="handleSaveFilterSettings"
          @close="closeFilterSettings"
        />
        <DashboardSettingsWizard
          v-if="openDashboardSettings && dashboardsManager.activeDashboard.value"
          :active-dashboard-id="dashboardsManager.activeDashboardKey.value!.name"
          :dashboard-general-settings="
            deepClone(dashboardsManager.activeDashboard.value!.model.general_settings)
          "
          :dashboard-restricted-to-single="
            dashboardsManager.activeDashboard.value!.model.filter_context.restricted_to_single!
          "
          :logged-in-user="logged_in_user"
          :permissions="permissions"
          @cancel="openDashboardSettings = false"
          @save="updateDashboardSettings"
        />
      </div>
      <div class="db-app__content">
        <template v-if="dashboardsManager.isInitialized.value && !loadingError">
          <CloneSuccessAlert
            v-model:open="showCloneSuccessAlert"
            :has-filters="dashboardHasFilters"
            @edit-filters="openDashboardFilterSettings = true"
          />
          <AddWidgetPage
            v-if="Object.entries(dashboardWidgets.widgetCores.value).length === 0"
            :workflow-items="dashboardWidgetWorkflows"
            :dashboard-features="available_features.dashboard_features"
            @select="handleAddWidget"
          />
          <DashboardComponent
            v-else-if="dashboardsManager.isInitialized.value"
            :key="`${dashboardsManager.activeDashboardKey.value?.owner}-${dashboardsManager.activeDashboardKey.value?.name}`"
            v-model:dashboard="dashboardsManager.activeDashboard.value!.model"
            :dashboard-key="dashboardsManager.activeDashboardKey.value!"
            :base-filters="dashboardFilters.baseFilters"
            :widget-cores="dashboardWidgets.widgetCores"
            :updated-widget-render-keys="dashboardWidgets.updatedWidgetRenderKeys"
            :widget-titles="widgetTitles"
            :is-editing="isDashboardEditingMode"
            @widget:edit="editWidget($event)"
            @widget:delete="dashboardWidgets.deleteWidget($event)"
            @widget:clone="(oldWidgetId, newLayout) => cloneWidget(oldWidgetId, newLayout)"
          />
        </template>
        <template v-else>
          <AddWidgetPage
            v-if="openDashboardCreationDialog || openDashboardCloneDialog"
            :workflow-items="dashboardWidgetWorkflows"
            :dashboard-features="available_features.dashboard_features"
          />
          <CmkErrorAlert v-if="loadingError" :error="loadingError" />
          <CmkIcon
            v-else-if="isCloning || !(openDashboardCreationDialog || openDashboardCloneDialog)"
            name="load-graph"
            size="xxlarge"
          />
        </template>
      </div>
    </div>
  </CmkErrorBoundary>
</template>

<style scoped>
.db-app--empty {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.db-app--empty .db-app__header {
  flex-shrink: 0;
}

.db-app--empty .db-app__content {
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
}

@media (width < 800px) {
  .db-app--empty .db-app__content {
    overflow-y: auto;
  }
}
</style>

<style>
.db-app--empty-dashboard {
  overflow: hidden;
  scrollbar-width: none;
}

.db-app--empty-dashboard::-webkit-scrollbar {
  display: none;
}
</style>
